import { useContext, useState, useEffect } from "react";
import currency from "currency.js";
import { Box, Grid, Typography } from "@mui/material";
import { UserContext } from "../../context/UserContext";
import SalesTax from "./SalesTax";
import {
  isSalesTaxEnabled,
  hasOwnerAccess,
  recalculatePayment,
  getCashPaymentDiscountTotal,
  isCostPlusFeesModel,
} from "../../common/util";
import Discount from "./Discount";

export default function PaymentEntry(props) {
  const { user } = useContext(UserContext);
  const { items, fulfillment, payment, acceptDeposit, deposit, adjustments, paymentMethod } = props;

  const canToggleSalesTax =
    isSalesTaxEnabled(user.config) &&
    (hasOwnerAccess(user.role) || user.role === "manager");

  const [collectSalesTax, setCollectSalesTax] = useState(() => {
    if (props.collectSalesTax !== undefined) {
      return props.collectSalesTax;
    }
    return +payment?.salesTax > 0 ? true : isSalesTaxEnabled(user.config);
  });
  const [discount, setDiscount] = useState(() => +payment?.discount || 0);

  const fees = user.config?.fees;
  const isCashDiscountEnabled =
    paymentMethod === "cash" && isCostPlusFeesModel(fees) && +fees?.net > 0;

  const cashDiscount = isCashDiscountEnabled ? getCashPaymentDiscountTotal(items, fees) : 0;
  console.log("cashDiscount", isCashDiscountEnabled, cashDiscount);

  useEffect(() => {
    if (props.collectSalesTax !== undefined) {
      setCollectSalesTax(props.collectSalesTax);
    }
  }, [props.collectSalesTax]);

  useEffect(() => {
    const totalDiscount = currency(discount || 0).add(cashDiscount).value;
    // Order-level master switch: off = no tax (e.g. wholesale). On = product-level tax only.
    const salesTaxConfig =
      isSalesTaxEnabled(user.config) && collectSalesTax ? user.config?.salesTax : null;
    const recalc = recalculatePayment(
      items,
      fulfillment,
      totalDiscount,
      acceptDeposit,
      deposit,
      salesTaxConfig
    );
    props.onChange({
      ...recalc,
      discount,
      cashDiscount,
    });
  }, [items, fulfillment, deposit, collectSalesTax, discount, cashDiscount]);

  useEffect(() => {
    if (adjustments?.discount) {
      setDiscount(adjustments?.discount?.value || 0);
    }
  }, [adjustments]);

  const handleCollectSalesTaxChange = (checked) => {
    setCollectSalesTax(checked);
    props.onCollectSalesTaxChange?.(checked);
  };

  const discountMax = Math.max(0, currency(payment?.subtotal || 0).subtract(cashDiscount).value);

  const totalDiscountShown = currency(payment?.discount || 0).add(payment?.cashDiscount || 0).value;

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={9}>
          <Typography variant="overline" color="textSecondary">
            Subtotal
          </Typography>
        </Grid>
        <Grid item xs={3} align="right">
          <Typography variant="overline" color="textSecondary">
            {currency(payment?.subtotal).format()}
          </Typography>
        </Grid>
      </Grid>
      {cashDiscount > 0 && (
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={9}>
            <Typography variant="overline" color="textSecondary">
              Cash Discount
            </Typography>
          </Grid>
          <Grid item xs={3} align="right">
            <Typography variant="overline" color="textSecondary">
              ({currency(cashDiscount).format()})
            </Typography>
          </Grid>
        </Grid>
      )}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={9}>
          <Discount
            value={discount}
            max={discountMax}
            onChange={(value) => {
              setDiscount(value);
            }}
          />
        </Grid>
        <Grid item xs={3} align="right">
          <Typography variant="overline" color="textSecondary">
            ({currency(discount).format()})
          </Typography>
        </Grid>
      </Grid>
      {totalDiscountShown > 0 && (
        <Box sx={{ borderTop: "1px solid #DDD", paddingTop: "10px", marginTop: "10px" }}>
          <Grid container spacing={2}>
            <Grid item xs={9}>
              <Typography variant="overline" color="textSecondary">
                Discounted Subtotal
              </Typography>
            </Grid>
            <Grid item xs={3} align="right">
              <Typography variant="overline" color="textSecondary">
                {currency(payment?.subtotal || 0)
                  .subtract(payment?.discount || 0)
                  .subtract(payment?.cashDiscount || 0)
                  .format()}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}
      {payment?.delivery > 0 && (
        <Grid container spacing={2}>
          <Grid item xs={9}>
            <Typography variant="overline" color="textSecondary">
              Delivery fee
            </Typography>
          </Grid>
          <Grid item xs={3} align="right">
            <Typography variant="overline" color="textSecondary">
              {currency(payment?.delivery).format()}
            </Typography>
          </Grid>
        </Grid>
      )}
      {user.config?.salesTax && (
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={9}>
            {canToggleSalesTax ? (
              <SalesTax
                value={payment?.salesTax}
                include={collectSalesTax}
                onChange={handleCollectSalesTaxChange}
              />
            ) : (
              <Typography variant="overline" color="textSecondary">
                Sales Tax
              </Typography>
            )}
          </Grid>
          <Grid item xs={3} align="right">
            <Typography variant="overline" color="textSecondary">
              {currency(payment?.salesTax).format()}
            </Typography>
          </Grid>
        </Grid>
      )}
      {+payment?.tip > 0 && (
        <Grid container spacing={2}>
          <Grid item xs={9}>
            <Typography variant="overline" color="textSecondary">
              Tip
            </Typography>
          </Grid>
          <Grid item xs={3} align="right">
            <Typography variant="overline" color="textSecondary">
              {currency(payment?.tip).format()}
            </Typography>
          </Grid>
        </Grid>
      )}
      <Box sx={{ borderTop: "1px solid #DDD", paddingTop: "10px", marginTop: "10px" }}>
        <Grid container spacing={2}>
          <Grid item xs={9}>
            <Typography variant="overline" color="textSecondary">
              TOTAL (USD)
            </Typography>
          </Grid>
          <Grid item xs={3} align="right">
            <Typography variant="overline" color="textSecondary">
              {currency(payment?.total).format()}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
